import { NetworkStatus, useQuery, useReactiveVar } from '@apollo/client';
import {
  IColumn,
  IDetailsRowBaseProps,
  Modal,
  Stack,
  Text,
} from '@fluentui/react';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData } from 'common/components/SearchBar';
import {
  LISTVIEW_MODAL_HEIGHT,
  LISTVIEW_MODAL_WIDTH,
  TABLE_ROWS,
} from 'common/constants';
import { useCommonStyles } from 'common/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TripBreakdownSearch_tripBreakdownSearch_nodes } from 'travelAuthorization/TripBreakdown/list/__generated__/TripBreakdownSearch';
import { getColumns } from 'travelAuthorization/TripBreakdown/list/columns.data';
import { globalMode, setGlobalSearchText } from 'utility/cache/ui';

import { AmountColumnTextView } from 'common/components/AmountView/AmountColumnTextView';
import { CloseButton } from 'common/components/Buttons';
import { FilterArrayType, FilterCombineType } from 'common/components/Filters';
import {
  HighLightActiveLink,
  HighlightTextView,
} from 'common/components/HighLight';
import { StatusIcon } from 'common/components/StatusIcon';
import { SortOrder } from 'common/utils/commonTypes';
import { convertToTitleCase } from 'common/utils/convertToTitleCase';
import { ExportTrips } from 'travelAuthorization/TripBreakdown/list/ExportTrips';
import OverBudget from 'travelAuthorization/TripBreakdown/list/OverBudget';
import { SearchBarSection } from 'travelAuthorization/TripBreakdown/list/SearchBarSection';
import {
  handleColumnClick,
  performTripBreakdownSearch,
  transformTripBreakdownData,
} from 'travelAuthorization/TripBreakdown/list/listComponents';
import {
  isFilteredTripListVar,
  setCurrentProfile,
  setUserDefaults,
  travelerDialogVisibleVar,
} from 'utility/cache/ui';
import { TripBreakDownDetailsModal } from './TripBreakDownDetailsModal';
import TRIP_BREAKDOWN_SEARCH_TRAVELER from './TripBreakdownSearchTraveler.graphql';
import {
  TripBreakdownSearchTraveler,
  TripBreakdownSearchTravelerVariables,
} from './__generated__/TripBreakdownSearchTraveler';
import { useStyles } from './index.styles';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
export type TripBreakdownItem = TripBreakdownSearch_tripBreakdownSearch_nodes;
export const TripBreakDownListModal: React.FC<{
  isTravelerEntry?: boolean;
}> = ({ isTravelerEntry = false }) => {
  const styles = useStyles();
  const globalState = useReactiveVar(globalMode);
  const commonStyles = useCommonStyles();
  const currentProfileData = useReactiveVar(setCurrentProfile);
  const useDefaultsData = useReactiveVar(setUserDefaults);
  const columns = getColumns(currentProfileData, useDefaultsData);
  const [gridColumns, setGridColumns] = useState<ColumnData[]>(columns);
  const globalSearchText = useReactiveVar(setGlobalSearchText);

  const travelerDialogVisibleDefault = useReactiveVar(travelerDialogVisibleVar);

  const { data, networkStatus, loading, fetchMore, refetch } = useQuery<
    TripBreakdownSearchTraveler,
    TripBreakdownSearchTravelerVariables
  >(TRIP_BREAKDOWN_SEARCH_TRAVELER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    variables: {
      filter: {
        travelerId: {
          equalTo: travelerDialogVisibleDefault,
        },
      },
      first: TABLE_ROWS,
      orderBy: ['TRAVEL_AUTHORIZATION_NUMBER_DESC', 'PRIMARY_KEY_ASC'],
    },
  });

  const { nodes, pageInfo } = { ...data?.tripBreakdownSearch };
  const { sum } = { ...data?.tripBreakdownSearch?.aggregates };

  const refetching = loading && networkStatus !== NetworkStatus.fetchMore;
  const showFooter =
    !refetching &&
    sum &&
    Object.entries(sum).reduce((prev, [_, value]) => {
      return parseFloat(value || '0.0') > 0 || prev;
    }, false);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [selectedRows, setSelectedRows] = useState<TripBreakdownItem[]>([]);

  const [searchFilters, setSearchFilters] = useState<FilterArrayType[]>([]);

  const updatedSearchFilters = useMemo(
    () =>
      travelerDialogVisibleDefault
        ? [
            ...searchFilters,
            {
              value: travelerDialogVisibleDefault,
              filterKey: 'travelerId',
              label: 'Traveler',
              combineType: FilterCombineType.AND,
            },
          ]
        : searchFilters,
    [searchFilters, travelerDialogVisibleDefault]
  );
  const [searchTextValue, setSearchTextValue] = useState<string | null>(null);
  const transformedItems = useMemo(
    () => (refetching ? [] : transformTripBreakdownData(nodes)),
    [nodes, refetching]
  );

  const [selectedItem, setSelectedItem] = useState<
    TripBreakdownSearch_tripBreakdownSearch_nodes | undefined
  >(undefined);

  const handleSearchGlobal = (showMore: boolean, globalValue: boolean) => {
    performTripBreakdownSearch({
      showMore,
      globalValue,
      searchTextValue,
      searchFilters: updatedSearchFilters,
      sortOrderParam,
      data,
      fetchMore,
      refetch,
    });
  };

  const handleSearch = (showMore: boolean) =>
    handleSearchGlobal(showMore, globalState);

  const handleSearchMemo = useCallback(handleSearch, [
    searchTextValue,
    sortOrderParam,
    searchFilters,
  ]);
  const _onColumnClick = useCallback(
    (_ev?: React.MouseEvent<HTMLElement>, clickedColumn?: ColumnData) => {
      handleColumnClick(
        clickedColumn,
        gridColumns,
        setGridColumns,
        setSortOrderParam
      );
    },
    [gridColumns]
  );
  const _renderItemColumn = (
    item: TripBreakdownItem | undefined,
    _index: number | undefined,
    column: IColumn | undefined
  ) => {
    if (item) {
      const fieldContent = item[
        column?.fieldName as keyof TripBreakdownItem
      ] as string;

      switch (column?.key) {
        case 'tripLocator':
          return (
            <Stack verticalAlign="center" className={styles.rowContainer}>
              <HighLightActiveLink
                highlightText={globalSearchText}
                text={fieldContent}
                preventDefault={true}
                onLinkClick={() => {
                  setSelectedItem(item);
                }}
              />
            </Stack>
          );
        case '_spotCurrencyAmount':
        case '_baseCurrencyAmount':
        case '_companionAmountUsed':
          return (
            <AmountColumnTextView
              value={fieldContent}
              searchText={globalSearchText}
            />
          );

        case '_isCorporateTravelAuthorization':
          return (
            <Stack verticalAlign="center" className={styles.rowContainer}>
              <HighlightTextView
                highlightText={globalSearchText}
                text={fieldContent ? 'Corporate' : 'Production'}
              />
            </Stack>
          );
        case '_companionTicketUsed':
          return (
            <AmountColumnTextView
              value={fieldContent?.toString()}
              searchText={globalSearchText}
              isDecimal={false}
            />
          );
        case 'incidentalAmount':
          return (
            <OverBudget
              isAmountExist={item._isIncidentalExist}
              amount={item.incidentalAmount}
              budgetAmount={item.incidentalBudgetAmount}
              globalSearchText={globalSearchText!}
              isOverBudget={item._isIncidentalOverBudget}
            />
          );
        case 'trainAmount':
          return (
            <OverBudget
              isAmountExist={item._isTrainExist}
              amount={item.trainAmount}
              budgetAmount={item.trainBudgetAmount}
              globalSearchText={globalSearchText!}
              isOverBudget={item._isTrainOverBudget}
            />
          );
        case 'transportationAmount':
          return (
            <OverBudget
              isAmountExist={item._isTransportationExist}
              amount={item.transportationAmount}
              budgetAmount={item.transportationBudgetAmount}
              globalSearchText={globalSearchText!}
              isOverBudget={item._isTransportationOverBudget}
            />
          );

        case 'vehicleRentalAmount':
          return (
            <OverBudget
              isAmountExist={item._isVehicleRentalExist}
              amount={item.vehicleRentalAmount}
              budgetAmount={item.vehicleRentalBudgetAmount}
              globalSearchText={globalSearchText!}
              isOverBudget={item._isVehicleRentalOverBudget}
            />
          );
        case 'accommodationAmount':
          return (
            <OverBudget
              isAmountExist={item._isAccommodationExist}
              amount={item.accommodationAmount}
              budgetAmount={item.accommodationBudgetAmount}
              globalSearchText={globalSearchText!}
              isOverBudget={item._isAccommodationOverBudget}
            />
          );

        case 'airfareAmount':
          return (
            <OverBudget
              isAmountExist={item._isAirfareExist}
              amount={item.airfareAmount}
              budgetAmount={item.airfareBudgetAmount}
              globalSearchText={globalSearchText!}
              isOverBudget={item._isAirfareOverBudget}
            />
          );

        case 'controlTotalAmount':
          return (
            <OverBudget
              isAmountExist={
                !(
                  item.controlTotalAmount === null ||
                  item.controlTotalAmount === '0.00'
                )
              }
              amount={item.controlTotalAmount}
              budgetAmount={item.budgetAmount}
              globalSearchText={globalSearchText!}
              isOverBudget={item._isOverBudget}
            />
          );
        case '_taStatusType':
          return (
            <Stack horizontal verticalAlign="center">
              <HighlightTextView
                className={styles.statusType}
                highlightText={globalSearchText}
                text={convertToTitleCase(item?._taStatusType!)!}
              />
              <Stack>
                <StatusIcon
                  approval={false}
                  iconType={{ ...item.statusType }}
                />
              </Stack>
            </Stack>
          );
        default:
          return (
            <Stack verticalAlign="center" className={styles.rowContainer}>
              {!!fieldContent && (
                <HighlightTextView
                  highlightText={globalSearchText}
                  text={fieldContent}
                />
              )}
            </Stack>
          );
      }
    }
  };

  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    handleSearchMemo(false);
  }, [handleSearchMemo, searchTextValue, sortOrderParam, searchFilters]);

  const _renderDetailsFooterItemColumn: IDetailsRowBaseProps['onRenderItemColumn'] =
    (_item, _index, column) => {
      const { aggregates } = {
        ...data?.tripBreakdownSearch,
      };

      let fieldContent;

      switch (column?.key) {
        case 'airfareAmount':
          fieldContent = aggregates?.sum?.airfareAmount;
          break;
        case 'accommodationAmount':
          fieldContent = aggregates?.sum?.accommodationAmount;
          break;
        case 'vehicleRentalAmount':
          fieldContent = aggregates?.sum?.vehicleRentalAmount;
          break;
        case 'transportationAmount':
          fieldContent = aggregates?.sum?.transportationAmount;
          break;
        case 'trainAmount':
          fieldContent = aggregates?.sum?.trainAmount;
          break;
        case 'incidentalAmount':
          fieldContent = aggregates?.sum?.incidentalAmount;
          break;
        case 'controlTotalAmount':
          fieldContent = aggregates?.sum?.controlTotalAmount;
          break;
        case '_companionAmountUsed':
          fieldContent = aggregates?.sum?._companionAmountUsed;
          break;
        case '_companionTicketUsed':
          fieldContent = aggregates?.sum?._companionTicketUsed;
          break;
        default:
          fieldContent = '';
      }

      return (
        <AmountTextView
          variant="medium"
          className={styles.footerTotal}
          value={fieldContent || ''}
        />
      );
    };
  return (
    <>
      <Modal isOpen={!selectedItem}>
        <Stack
          tokens={{ childrenGap: 20 }}
          className={commonStyles.listHeaderContainer}
          style={{
            height: LISTVIEW_MODAL_HEIGHT,
            width: LISTVIEW_MODAL_WIDTH,
          }}
        >
          <Stack
            horizontal
            verticalAlign="center"
            className={commonStyles.listTitleContainer}
          >
            <Text variant="xLarge">Trip Breakdown </Text>
            <Stack style={{ marginLeft: 'auto', marginRight: 20 }}>
              <ExportTrips
                selectedRows={selectedRows}
                isSelectedAll={
                  !!transformedItems?.length &&
                  selectedRows.length === transformedItems.length
                }
                searchFilters={searchFilters}
                isLinkSearch={globalState}
                tripBreakdownDocumentSearchRequest={
                  searchTextValue ? { searchText: [searchTextValue] } : null
                }
              />
            </Stack>
            <Stack>
              <CloseButton
                onClick={() => {
                  globalMode(false);
                  isFilteredTripListVar(false);
                  if (isTravelerEntry) travelerDialogVisibleVar(undefined);
                }}
              />
            </Stack>
          </Stack>

          <>
            <SearchBarSection
              columns={gridColumns}
              onToggleVisibility={setGridColumns}
              onRefresh={() => {
                handleSearch(false);
              }}
              onFilterChange={setSearchFilters}
              onEnterPress={(value) => {
                setSearchTextValue(value);
                setGlobalSearchText(value);
              }}
              isTripBreakdownListModal
            />

            <InfiniteList
              items={transformedItems}
              loading={loading}
              hasNextPage={pageInfo?.hasNextPage}
              showFooter={!!showFooter}
              columns={gridColumns.filter((column) => column.isVisible)}
              onRenderFooterItemColumn={_renderDetailsFooterItemColumn}
              onRenderItemColumn={_renderItemColumn}
              onColumnHeaderClick={_onColumnClick}
              onSelectionChanged={setSelectedRows}
              onLoadMore={() => handleSearch(true)}
            />
          </>
        </Stack>
      </Modal>
      {selectedItem && (
        <TripBreakDownDetailsModal
          selectedItem={selectedItem}
          onClose={() => {
            setSelectedItem(undefined);
          }}
        />
      )}
    </>
  );
};
